import type { JournalEntry, JournalStore } from './engine';

/**
 * Durable journal of in-doubt mutating actions, backed by IndexedDB — the
 * WorkLogix kiosk queue pattern (web-kiosk/src/device/queue.ts) applied to the
 * netShim's `jjbwms.pending` journal. Every write's idempotency key is
 * persisted BEFORE the network attempt and dropped once its fate is known;
 * whatever is still here on load gets probe-settled (engine.settle()).
 *
 * Only mutating envelopes ever land here. Reads are never journalled.
 */

const DB_NAME = 'warehouselogix-floor';
const DB_VERSION = 1;
const STORE = 'pending-writes';
/** The journal is a dead-letter ledger, not a queue of work — keep it small. */
const MAX_ENTRIES = 40;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise !== null) {
    return dbPromise;
  }
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'ik' });
      }
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open the floor IndexedDB.'));
    };
  });
  return dbPromise;
}

function store(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB request failed.'));
    };
  });
}

/** The browser {@link JournalStore}. Construction is cheap; the DB opens lazily. */
export class IdbJournal implements JournalStore {
  async add(entry: JournalEntry): Promise<void> {
    const db = await openDb();
    await promisify(store(db, 'readwrite').put(entry));
    await this.trim(db);
  }

  async drop(ik: string): Promise<void> {
    const db = await openDb();
    await promisify(store(db, 'readwrite').delete(ik));
  }

  async list(): Promise<JournalEntry[]> {
    const db = await openDb();
    const all = await promisify(store(db, 'readonly').getAll() as IDBRequest<JournalEntry[]>);
    return all.sort((a, b) => a.ts - b.ts);
  }

  /** Cap the ledger (netShim kept its last 20; we keep 40 to be generous). */
  private async trim(db: IDBDatabase): Promise<void> {
    const all = await promisify(store(db, 'readonly').getAll() as IDBRequest<JournalEntry[]>);
    if (all.length <= MAX_ENTRIES) {
      return;
    }
    const oldestFirst = all.sort((a, b) => a.ts - b.ts);
    const excess = oldestFirst.slice(0, all.length - MAX_ENTRIES);
    for (const e of excess) {
      await promisify(store(db, 'readwrite').delete(e.ik));
    }
  }
}
